import React, { use, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import Button from '../../../../../shared/components/ui/Button';
import FormInput from '../../../../../shared/components/ui/FormInput';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';
import {
  getReportsHelpRequests,
  addReportsHelpRequest
} from '../../../../services/api/implementation/impl';

const Section9 = ({
  mode,
  collapsedSections,
  renderSectionHeader,
  projectID,
  month,
  canAddReason,
  sectionNote
}) => {
  const userID = Number(Cookies.get('userId'));

  // Form for adding help requests in dashboard mode
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  // API Query for Help Requests (only in view/meeting modes)
  const {
    data: helpRequestsApiData,
    isLoading: isLoadingHelpRequests,
    refetch: refetchHelpRequests
  } = useQuery({
    queryKey: ['helpRequests', projectID, month, mode],
    queryFn: () => getReportsHelpRequests(projectID, month),
    enabled: !!projectID && !!month && (mode === 'view' || mode === 'meeting')
  });

  useEffect(() => {
    refetchHelpRequests();
  }, []);

  // Mutation for creating help requests
  const { mutate: createHelpRequest, isPending: isSubmittingHelpRequest } = useMutation({
    mutationFn: (payload) => addReportsHelpRequest(payload),
    onSuccess: () => {
      toast.success('Help request added successfully');
      reset();
      refetchHelpRequests();
      mixpanel.track('Help Request Added', {
        user_id: userID,
        project_id: projectID,
        month: month,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add help request');
      mixpanel.track('Help Request Failed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Handle form submission
  const onSubmitHelpRequest = (data) => {
    if (!projectID || !month) {
      toast.error('Please select project and month');
      return;
    }

    const currentYear = new Date().getFullYear();
    const payload = {
      title: data.title, // Textarea value goes to title
      description: data.title, // Also set description same as title
      project_id: projectID,
      month: month,
      year: currentYear
    };

    createHelpRequest(payload);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {renderSectionHeader(10, 'Sales Manager Help Requests (To Enhance Next Month Performance)')}
      {(mode === 'view' || !collapsedSections[9]) && (
        <>
          {/* Dashboard Mode: Show form to add requests */}
          {mode === 'dashboard' && canAddReason && (
            <form onSubmit={handleSubmit(onSubmitHelpRequest)} className="mb-6">
              <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <FormInput
                  width={100}
                  label="Request"
                  register={register}
                  register_key="title"
                  isRequired={true}
                  type="textarea"
                  validation={{ required: 'Request is required' }}
                  errors={errors}
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={isSubmittingHelpRequest || !projectID || !month}>
                    {isSubmittingHelpRequest ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* View/Meeting Mode: Show list of requests */}

          <div className="space-y-4">
            {isLoadingHelpRequests ? (
              <div className="flex justify-center items-center h-[8rem]">
                <p className="text-sm text-gray-500">Loading help requests...</p>
              </div>
            ) : helpRequestsApiData?.data?.length === 0 ? (
              <div className="flex justify-center items-center h-[8rem]">
                <p className="text-sm text-gray-500">No help requests available</p>
              </div>
            ) : (
              helpRequestsApiData?.data?.map((request, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900">
                        {request.user?.name || request.userName || request.requester || 'N/A'}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {request.created_at
                        ? new Date(request.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })
                        : 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {request.title || request.description || 'N/A'}
                  </p>
                </div>
              ))
            )}
          </div>
        </>
      )}
      {/* Section Note */}
      {sectionNote && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  {sectionNote.user?.vertical || 'Management'} notes
                </h4>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-600">By</span>
                  <span className="text-xs font-medium text-gray-900">
                    {sectionNote.user?.name || 'Unknown User'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{sectionNote.note}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section9;
